import { useState } from "react";
import { ArrowUpRight, Maximize2, Zap } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { FadeUp } from "@/components/motion/Reveal";
import { ProjectModal } from "@/components/ProjectModal";
import { projects } from "@/data/projects";

export function References() {
  const { t, lang } = useLanguage();
  const [active, setActive] = useState(null);

  const openProject = (p) => {
    window.open(p.detailsUrl, "_blank", "noopener,noreferrer");
    setActive(p);
  };

  return (
    <section id="referenzen" data-testid="references-section" className="bg-navy-deep py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          dark
          index={t.sections.references.index}
          label={t.sections.references.label}
          title={t.sections.references.title}
          sub={t.sections.references.sub}
        />

        <div className="grid gap-6 md:grid-cols-2">
          {projects.map((p, i) => (
            <FadeUp key={p.slug} delay={(i % 2) * 0.1}>
              <article
                data-testid={`project-card-${p.slug}`}
                className="group flex h-full flex-col border border-white/10 bg-white/[0.03] transition-colors duration-300 hover:border-brand/60"
              >
                <button
                  onClick={() => openProject(p)}
                  data-testid={`project-image-${p.slug}`}
                  className="relative block aspect-[16/10] w-full cursor-pointer overflow-hidden text-left"
                  aria-label={p.title[lang]}
                >
                  <img
                    src={p.image}
                    alt={p.title[lang]}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 to-transparent" />
                  <span className="absolute left-5 top-5 border border-white/25 bg-navy-deep/60 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white backdrop-blur-sm">
                    {p.type[lang]}
                  </span>
                  <span className="absolute bottom-5 right-5 border border-white/25 bg-navy-deep/60 px-3 py-1.5 font-mono text-[11px] tracking-[0.1em] text-white backdrop-blur-sm">
                    {p.area}
                  </span>
                </button>

                <div className="flex flex-1 flex-col p-7 md:p-8">
                  <h3 className="mb-4 font-display text-xl font-bold tracking-tight text-white md:text-2xl">
                    {p.title[lang]}
                  </h3>
                  <div className="mb-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
                    <span className="flex items-center gap-1.5">
                      <Maximize2 className="h-4 w-4 text-brand" /> {p.area}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Zap className="h-4 w-4 text-brand" /> {p.scope[lang]}
                    </span>
                  </div>
                  <p className="mb-8 flex-1 text-sm leading-relaxed text-white/70">{p.excerpt[lang]}</p>
                  <button
                    onClick={() => openProject(p)}
                    data-testid={`project-view-button-${p.slug}`}
                    className="group/btn inline-flex w-fit items-center gap-2 border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white hover:bg-white hover:text-navy"
                  >
                    {t.references.viewProject}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </button>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>

      <ProjectModal project={active} onClose={() => setActive(null)} />
    </section>
  );
}
